<script setup lang="ts">
// Date-range filter. Two `<input type="date">` controls side by side
// covering start and end. Emits `[start, end]` as ISO date strings, or
// `null` whenever either bound is empty — authors can then branch on
// "no range applied" cleanly.

import { computed, useId } from "vue";

import type { DateRangeOpts } from "../sbi/protocol";
import { usePrintMode } from "../sbi/print-mode.ts";
import PrintStaticControl from "./PrintStaticControl.vue";
import ControlLabel from "./ControlLabel.vue";

const props = defineProps<{
  id: string;
  value: readonly [string, string] | null;
  opts?: DateRangeOpts;
}>();

const emit = defineEmits<{
  (
    e: "change-target",
    id: string,
    value: [string, string] | null,
  ): void;
}>();

const start = computed(() => props.value?.[0] ?? "");
const end = computed(() => props.value?.[1] ?? "");

const isPrint = usePrintMode();

// Label wiring: ControlLabel puts `labelId` on the label text.
const labelId = useId();

// Print-mode display: "2026-01-01 → 2026-03-31". The arrow disambiguates
// the range from the "X to Y" axis-tick convention readers might pull
// in from a chart on the same page.
const printValue = computed(() => {
  if (!props.value) return "(not set)";
  return `${props.value[0]} → ${props.value[1]}`;
});

function emitNext(s: string, e: string): void {
  if (s === "" || e === "") {
    emit("change-target", props.id, null);
    return;
  }
  emit("change-target", props.id, [s, e]);
}

function onStart(e: Event): void {
  emitNext((e.target as HTMLInputElement).value, end.value);
}
function onEnd(e: Event): void {
  emitNext(start.value, (e.target as HTMLInputElement).value);
}
</script>

<template>
  <PrintStaticControl
    v-if="isPrint()"
    :label="opts?.label"
    :value="printValue"
  />
  <div v-else class="date-range">
    <ControlLabel
      v-if="opts?.label || opts?.description"
      class="date-range-label"
      :label="opts?.label"
      :description="opts?.description"
      :label-id="labelId"
    />
    <div
      class="inputs"
      role="group"
      :aria-labelledby="opts?.label ? labelId : undefined"
    >
      <input
        type="date"
        :value="start"
        :min="opts?.min"
        :max="opts?.max"
        aria-label="Start date"
        @change="onStart"
      />
      <span class="sep" aria-hidden="true">→</span>
      <input
        type="date"
        :value="end"
        :min="opts?.min"
        :max="opts?.max"
        aria-label="End date"
        @change="onEnd"
      />
    </div>
  </div>
</template>

<style scoped>
/* Never wider than the slot it sits in; full width when that slot is
   narrow (filter bar < 480px, a narrow column cell). */
.date-range {
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  display: inline-flex;
  flex-direction: column;
  gap: 4px;
  font-size: 13px;
}
@container (max-width: 479px) {
  .date-range {
    display: flex;
    width: 100%;
  }
}
.date-range-label {
  color: var(--color-text-muted);
  font-size: 12px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.inputs {
  display: flex;
  align-items: center;
  gap: 8px;
  max-width: 100%;
  min-width: 0;
}
/* Narrow: two equal columns either side of the separator, so the pair
   never overflows the slot (two native date inputs need ~310px). */
@container (max-width: 479px) {
  .inputs {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
    width: 100%;
  }
  .inputs input[type] {
    width: 100%;
  }
}
.sep {
  color: var(--color-text-muted);
  font-size: 12px;
}
input[type="date"] {
  background: var(--card);
  color: var(--card-ink);
  border: 1px solid var(--rule);
  border-radius: var(--radius);
  padding: 6px 10px;
  font: inherit;
  cursor: pointer;
  min-width: 0;
  flex: 1 1 auto;
  box-sizing: border-box;
}
input[type="date"]:focus {
  outline: 2px solid var(--primary);
  outline-offset: 1px;
}
/* Touch: 16px text (iOS zooms the page when a smaller input takes
   focus) and 40–44px targets. Desktop keeps the 13px density. */
@media (pointer: coarse) {
  input[type="date"] {
    min-height: 44px;
    font-size: 16px;
  }
}
</style>
